/**
 * @file Store declaration for the current logged in influencer tags.
 * @author Ida <max@groover.com>
 * @typedef { import("@/store/index").ActionContext } ActionContext
 * @typedef {string} TagTarget - Represents a tag type targeted by a specific action / getter / mutation, eg. "subgenres"
 */

import Vue from "vue"

import patcher_shorthand from "~/mixins/store/patcher"

import resetMixin from "~/mixins/store/resetToInitialState"

/**
 * State generator.
 *
 * @typedef {object} StoreUserInfluencerTagsState
 * @property {object} liked - A collection of liked tag types organised by arrays.
 * @property {Array.<number>} liked.subgenre - An array of liked subgenres organised.
 * @property {object} neutral - A collection of neutral tag types organised by arrays.
 * @property {Array.<number>} neutral.subgenre - An array of neutral subgenres organised.
 * @property {object} hated - A collection of hated tag types organised by arrays.
 * @property {Array.<number>} hated.subgenre - An array of hated subgenres organised.
 * @property {object} identity - A collection of tag types the identifies the current influencer.
 * @property {Array.<number>} identity.influencer_kind - An array of influencer_kind typed tag ids.
 * @property {Array.<number>} identity.country - An array of the influencers country. This array should never exceed the length of 1.
 * @summary Each member of the state represents a category of tags that could potentially hold shared types.
 * @returns {StoreUserInfluencerTagsState} Default state for influencer/tags.
 */
const initialState = function () {
  const common = {
    subgenre: [],
    mood: [],
    format: [],
    artist_kind: [],
    track_age: [],
  }

  return {
    liked: {
      ...common,
    },
    hated: {
      ...common,
    },
    neutral: {
      ...common,
    },
    identity: {
      influencer_kind: [],
      country: [],
    },
    exclusivity: {
      country: [],
      lyrics_lang: [],
    },
  }
}

export const state = initialState

/** @typedef {object} mutations */
export const mutations = {
  /**
   * This will apply a patch object to the supplied target key.
   *
   * @param {StoreUserInfluencerTagsState} state - The current state.
   * @param {object} context - An object holding the target and the patch object.
   * @param {string} context.target - The desired target the needs to be patched.
   * @param {object} context.patch - This will be looped on and if the key it holds exists and is the same type as in the state target it will overwrite the state with the new data.
   * @example
   * // Overwrites state.liked.subgenres to [69, 420]
   * SET_TARGET({ target : 'liked', { subgenres : [69, 420] } })
   * @example
   * // Overwrites identity.country to [69, 420] but skips the unknowed `foo` key
   * // It will also ignore it's sister key `influencer_kind` since it's not in the `patch` object
   * SET_TARGET({
   *   target : 'identity',
   *   patch : { country : [69, 420], foo : 'useless content' }
   * })
   * @returns {undefined}
   */
  SET_TARGET: function (state, { target, patch }) {
    target = target.toLowerCase()
    if (
      ["liked", "hated", "neutral", "identity", "exclusivity"].includes(target)
    ) {
      Vue.set(state, target, {
        ...patcher_shorthand(state, { patch, target }),
      })
    } else console.warn(`[INFLUENCER TAG] ${target} is not a valid target`)
  },
  ...resetMixin.mutations,
}

export const actions = {
  /**
   * Resets the state to it's default.
   *
   * @param { ActionContext } context - The context provided by vuex.
   * @returns {undefined}
   */
  RESET: function ({ commit }) {
    commit("RESET", initialState())
  },

  /**
   * Fetches the tags of the current influencer and fills the state accordingly.
   *
   * @param { ActionContext } vuexContext - The context provided by vuex.
   * @param { string } slug - An optional parameter for admins logged in to fetch and fill the state.
   * @returns {Promise} - Will always resolve, but if an error is encountered it will return null.
   */
  FETCH: function ({ commit, rootGetters, rootState }, slug) {
    return new Promise((resolve, reject) => {
      if (rootGetters["user/IS_INF"] || rootState.user.is_staff) {
        const baseUrl = "/influencer/tags/"
        const url = slug?.length ? `${baseUrl}?slug=${slug}` : baseUrl

        this.$axios
          .$get(url)
          .then((values) => {
            ["liked", "neutral", "hated", "exclusivity"].forEach((target) => {
              commit("SET_TARGET", {
                patch: values[target] ?? {},
                target,
              })
            })
            commit("SET_TARGET", {
              patch: {
                ...values.identity,
                influencer_kind: values.identity?.influencer_kind ?? [],
              },
              target: "identity",
            })
            resolve({ ...values })
          })
          .catch((err) => {
            console.log(err)
            reject(err)
          })
      } else resolve(null)
    })
  },
  /**
   * Updates influencer tags on the server.
   *
   * @param { ActionContext } _ - Unused.
   * @param {object} config - Configuration data for the action.
   * @param {object} config.patch - The data to be patched.
   * @param {number} config.influencer_id - An optional parameter to allow admin users to update the desired content from the front app.
   * @returns {Promise} Will resolve and return server data on 2XX and reject on !2XX with the axios error object as data.
   */
  UPDATE_FROM_PATCH: function (_, { patch, influencer_id }) {
    const baseUrl = "/influencer/tags/"
    const url = influencer_id ? `${baseUrl}?id=${influencer_id}` : baseUrl

    return new Promise((resolve, reject) => {
      this.$axios
        .post(url, patch)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
}

/**
 * @typedef {object} StoreUserInfluencerTagsGetters
 * @constant {StoreUserInfluencerTagsGetters}
 */
export const getters = {
  /**
   * Function generator to get dissallowed ids in the provided category.
   *
   * @param {StoreUserInfluencerTagsState} _ - Unused.
   * @param {StoreUserInfluencerTagsGetters} getters - The current scoped vuex state.
   * @param state
   * @returns {GETTERS_GET_DISSALOWED} A generated function to retrieve dissallowed ids in the provided category.
   */
  GET_DISALLOWED(state) {
    return (
      /**
       * Returns the corresponding tag ids from a configuration.
       *
       * @typedef {Function} GETTERS_GET_DISSALOWED
       * @param {string} category - A category of tags, eg. Liked, hated, or neutral. Caps unsensitive.
       * @param {TagTarget} target
       * @returns {Array<number>}
       */
      function (category, target) {
        category = category.toLowerCase()
        const allowed_keys = ["liked", "hated", "neutral"]
        const index = allowed_keys.indexOf(category)

        if (category === "exclusivity") return []
        else if (index !== -1) {
          allowed_keys.splice(index, 1)
          return [
            ...allowed_keys.reduce((accumulator, category_key) => {
              state[category_key][target].forEach((tag_id) =>
                accumulator.add(tag_id)
              )
              return accumulator
            }, new Set()),
          ]
        } else {
          console.warn(
            `[INFLUENCER TAGS]: ${category} is not a valid parameter in GET_DISALLOWED`
          )
          return []
        }
      }
    )
  },
  COUNTRY: (state) => state.identity.country[0] ?? 0,
  GET_FLATTEN_CATEGORY(state) {
    return function (category, tags = state) {
      category = category.toLowerCase()
      if (["liked", "hated", "neutral"].includes(category)) {
        return [
          ...Object.keys(tags[category]).reduce((accumulator, key) => {
            tags[category][key].forEach((tag_id) => {
              accumulator.add(tag_id)
            })
            return accumulator
          }, new Set()),
        ]
      } else return []
    }
  },
  GET_TAGS(state) {
    return function (category, target) {
      category = category.toLowerCase()
      if (
        ["liked", "hated", "neutral", "identity", "exclusivity"].includes(
          category
        )
      ) {
        if (typeof state[category][target] !== "undefined")
          return state[category][target]
        else
          console.warn(
            `[INFLUENCER TAGS]: ${target} is not a valid parameter in GET_CATEGORY`
          )
      } else
        console.warn(
          `[INFLUENCER TAGS]: ${category} is not a valid parameter in GET_CATEGORY`
        )
      return []
    }
  },
  IS_MENTOR(state) {
    for (let key in state.identity?.influencer_kind ?? []) {
      if (key.normalize().toLowerCase() === "mentor") {
        return true
      }
    }
    return false
  },
}
